import type { ComponentType, ReactNode } from 'react'
import { useSettingsEmbedded } from '@/components/shared/settings/settings-embed'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useOrganization } from '@/hooks/useOrganization'
import { cn } from '@/lib/utils'
import {
  ArrowRightIcon,
  LockSimpleIcon,
  SparkleIcon,
} from '@phosphor-icons/react'
import { Link } from 'react-router-dom'

import { getRequiredSubscriptionPlan } from '@scrambl/shared'

export interface MockColumn {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  className?: string
}

export interface MockRow {
  id: string
  /** Optional avatar — initials with a soft background */
  avatar?: {
    initials: string
    tone?: 'mint' | 'sky' | 'rose' | 'lilac' | 'amber' | 'slate'
  }
  /** Map of column key → cell content. Strings render plain; objects render as a status pill. */
  cells: Record<
    string,
    | ReactNode
    | { kind: 'pill'; label: string; tone: PillTone }
    | { kind: 'money'; value: string }
    | { kind: 'muted'; value: string }
  >
}

export type PillTone = 'mint' | 'sky' | 'amber' | 'rose' | 'slate' | 'lilac'

export interface ModuleLandingFeature {
  icon: ComponentType<{
    className?: string
    weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone'
  }>
  title: string
  body: string
}

export interface ModuleLandingConfig {
  /** Module slug — used for keying. */
  slug: string
  /** Eyebrow tag shown above the title (defaults to "Pro feature"). */
  eyebrow?: string
  /** Big editorial title (e.g. "Forms"). */
  title: string
  /**
   * What the closing strip unlocks when the title isn't a name — the generic
   * landing's title is a sentence ("Audit trail is part of Pro").
   */
  name?: string
  /** Subtitle paragraph (1-2 sentences). */
  subtitle: string
  /** Hero icon — large decorative phosphor or local icon. */
  icon: ComponentType<{
    className?: string
    weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone'
  }>
  /** Module's signature accent color used for subtle gradients. Use a Tailwind hue name. */
  accent:
    | 'amber'
    | 'rose'
    | 'emerald'
    | 'sky'
    | 'violet'
    | 'indigo'
    | 'teal'
    | 'orange'
    | 'fuchsia'
  /** 3-4 feature highlights. */
  features: ModuleLandingFeature[]
  /** Mock table preview (skip for catch-all). */
  preview?: {
    /** Title shown above the table (e.g. "Forms · 5 examples"). */
    label: string
    /** One sentence on what the example shows, under "{title} in action". */
    caption?: string
    columns: MockColumn[]
    rows: MockRow[]
  }
}

const AVATAR_TONES: Record<
  NonNullable<MockRow['avatar']>['tone'] & string,
  string
> = {
  mint: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  sky: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  lilac:
    'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300',
}

const PILL_TONES: Record<PillTone, string> = {
  mint: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/10 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20',
  sky: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-600/10 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20',
  amber:
    'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-600/15 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20',
  rose: 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-600/10 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20',
  slate:
    'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-600/10 dark:bg-slate-500/15 dark:text-slate-300 dark:ring-slate-500/20',
  lilac:
    'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-600/10 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20',
}

const ACCENT_GRADIENT: Record<ModuleLandingConfig['accent'], string> = {
  amber: 'from-amber-200/60 via-yellow-100/40 to-transparent',
  rose: 'from-rose-200/60 via-pink-100/40 to-transparent',
  emerald: 'from-emerald-200/60 via-teal-100/40 to-transparent',
  sky: 'from-sky-200/60 via-cyan-100/40 to-transparent',
  violet: 'from-violet-200/60 via-indigo-100/40 to-transparent',
  indigo: 'from-indigo-200/60 via-blue-100/40 to-transparent',
  teal: 'from-teal-200/60 via-emerald-100/40 to-transparent',
  orange: 'from-orange-200/60 via-amber-100/40 to-transparent',
  fuchsia: 'from-fuchsia-200/60 via-pink-100/40 to-transparent',
}

const ACCENT_ICON_BG: Record<ModuleLandingConfig['accent'], string> = {
  amber:
    'bg-amber-50 text-amber-600 ring-amber-200/60 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20',
  rose: 'bg-rose-50 text-rose-600 ring-rose-200/60 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20',
  emerald:
    'bg-emerald-50 text-emerald-600 ring-emerald-200/60 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20',
  sky: 'bg-sky-50 text-sky-600 ring-sky-200/60 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20',
  violet:
    'bg-violet-50 text-violet-600 ring-violet-200/60 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20',
  indigo:
    'bg-indigo-50 text-indigo-600 ring-indigo-200/60 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20',
  teal: 'bg-teal-50 text-teal-600 ring-teal-200/60 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-500/20',
  orange:
    'bg-orange-50 text-orange-600 ring-orange-200/60 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20',
  fuchsia:
    'bg-fuchsia-50 text-fuchsia-600 ring-fuchsia-200/60 dark:bg-fuchsia-500/10 dark:text-fuchsia-300 dark:ring-fuchsia-500/20',
}

function isPillCell(
  value: unknown,
): value is { kind: 'pill'; label: string; tone: PillTone } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    (value as { kind: string }).kind === 'pill'
  )
}
function isMoneyCell(
  value: unknown,
): value is { kind: 'money'; value: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    (value as { kind: string }).kind === 'money'
  )
}
function isMutedCell(
  value: unknown,
): value is { kind: 'muted'; value: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    (value as { kind: string }).kind === 'muted'
  )
}

function MockTable({
  preview,
  accent,
  planName,
}: {
  preview: NonNullable<ModuleLandingConfig['preview']>
  accent: ModuleLandingConfig['accent']
  planName: string
}) {
  const { columns, rows, label } = preview
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_0_rgba(0,0,0,0.02),0_24px_60px_-32px_rgba(15,23,42,0.18)] dark:border-white/5 dark:bg-slate-950">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-slate-200/80 px-5 py-3.5 dark:border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-300/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-300/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-300/80" />
          </div>
          <span className="ml-2 font-mono text-[11px] uppercase tracking-[0.12em] text-slate-400">
            {label}
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-amber-950">
          <SparkleIcon weight="fill" className="h-3 w-3" />
          {planName} Preview
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200/70 dark:border-white/5">
              {columns.map(col => (
                <th
                  key={col.key}
                  scope="col"
                  className={cn(
                    'px-5 py-3 text-left font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400',
                    col.align === 'right' && 'text-right',
                    col.align === 'center' && 'text-center',
                    col.className,
                  )}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={row.id}
                className={cn(
                  'border-b border-slate-100/80 transition-colors last:border-b-0 hover:bg-slate-50/60 dark:border-white/[0.04] dark:hover:bg-white/[0.02]',
                  // Stagger fade-in
                  'motion-safe:animate-[fadeUp_0.5s_ease-out_both]',
                )}
                style={{ animationDelay: `${idx * 60 + 200}ms` }}
              >
                {columns.map((col, colIdx) => {
                  const cell = row.cells[col.key]
                  const isFirst = colIdx === 0
                  return (
                    <td
                      key={col.key}
                      className={cn(
                        'whitespace-nowrap px-5 py-3.5 align-middle text-slate-700 dark:text-slate-300',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.align === 'center' && 'text-center',
                        col.className,
                      )}
                    >
                      {isFirst && row.avatar ? (
                        <div className="flex items-center gap-3">
                          <span
                            className={cn(
                              'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                              AVATAR_TONES[row.avatar.tone ?? 'slate'],
                            )}
                          >
                            {row.avatar.initials}
                          </span>
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {cell as ReactNode}
                          </span>
                        </div>
                      ) : isPillCell(cell) ? (
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                            PILL_TONES[cell.tone],
                          )}
                        >
                          {cell.label}
                        </span>
                      ) : isMoneyCell(cell) ? (
                        <span className="font-mono text-[13px] tabular-nums text-slate-900 dark:text-slate-100">
                          {cell.value}
                        </span>
                      ) : isMutedCell(cell) ? (
                        <span className="text-slate-400">{cell.value}</span>
                      ) : (
                        (cell as ReactNode)
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Soft fade at bottom */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t to-transparent',
          accent === 'amber' && 'from-amber-50/60',
          accent !== 'amber' && 'from-white/60 dark:from-slate-950/60',
        )}
      />
    </div>
  )
}

interface ModuleUpgradeLandingProps {
  config: ModuleLandingConfig
  featureKey: string
}

export function ModuleUpgradeLanding({
  config,
  featureKey,
}: ModuleUpgradeLandingProps) {
  const { currentOrganization } = useOrganization()
  const slug = currentOrganization?.slug ?? ''
  const HeroIcon = config.icon
  const isInSettingsCard = useSettingsEmbedded()
  const planName = getRequiredSubscriptionPlan(featureKey)?.displayName ?? 'Pro'
  const withPlanName = (value: string) => value.replace(/\bPro\b/g, planName)
  const title = withPlanName(config.title)
  const eyebrow = withPlanName(config.eyebrow ?? 'Pro feature')
  // A module's title is its name ("Unlock Forms and the rest of Business");
  // the generic title is a sentence, so it uses the feature's name or none.
  const unlockName =
    config.name ?? (config.slug === 'default' ? undefined : title)
  const unlockLine = unlockName
    ? `Unlock ${unlockName} and the rest of ${planName}.`
    : `Unlock everything in ${planName}.`
  // The landing stands in for the module page, so the tab names the module
  // ("Forms | Scrambl"); the generic landing's sentence-long title would not
  // fit, so it uses its eyebrow ("Business feature | Scrambl").
  // A generic landing that knows the feature's name uses it ("Audit trail").
  useDocumentTitle(config.name ?? (config.slug === 'default' ? eyebrow : title))

  return (
    // A size container: the landing renders full-page and inside panes (the
    // Settings shell), so the headline scales with its own width, not the
    // window's.
    // Inside the Settings card it's a panel of that card: card-tier corners
    // instead of a square block under the breadcrumb.
    <div
      className={cn(
        'relative h-full w-full overflow-y-auto bg-[#FAF7F1] [container-type:inline-size] dark:bg-slate-950',
        isInSettingsCard && 'rounded-card',
      )}
    >
      {/* Decorative grain + accent glow */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 h-[520px] bg-gradient-to-b',
          ACCENT_GRADIENT[config.accent],
        )}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-multiply dark:opacity-[0.06] dark:mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.6 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6 py-12 sm:px-10 sm:py-16 lg:py-20">
        {/* Eyebrow */}
        <div className="flex items-center gap-2 text-xs font-medium text-slate-500 motion-safe:animate-[fadeUp_0.5s_ease-out_both] dark:text-slate-400">
          <LockSimpleIcon weight="bold" className="h-3.5 w-3.5" />
          <span>{eyebrow}</span>
        </div>

        {/* Hero */}
        <div className="mt-6 grid gap-x-16 gap-y-10 [grid-template-columns:repeat(auto-fit,minmax(min(100%,24rem),1fr))]">
          <div
            className="motion-safe:animate-[fadeUp_0.6s_ease-out_both]"
            style={{ animationDelay: '80ms' }}
          >
            <h1 className="text-balance font-serif text-[clamp(2.5rem,5.5cqw,5rem)] font-medium leading-[0.95] tracking-[-0.025em] text-slate-900 dark:text-slate-50">
              {title}
            </h1>
            <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-slate-600 dark:text-slate-300 sm:text-lg">
              {withPlanName(config.subtitle)}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={`/${slug}/settings/administration/subscription/plans`}
                className="transition-tonal group inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 px-6 py-3 text-sm font-semibold text-amber-950 shadow-[0_2px_4px_rgba(180,140,40,0.25),inset_0_1px_0_rgba(255,255,255,0.5)] hover:scale-[1.02] hover:shadow-[0_8px_20px_rgba(180,140,40,0.35),inset_0_1px_0_rgba(255,255,255,0.5)] active:scale-[0.99]"
              >
                Upgrade to {planName}
                <ArrowRightIcon
                  weight="bold"
                  className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                />
              </Link>
              {/* On a phone it wraps under the button: no side padding there,
                  or its text sat 16px in from the button's edge. */}
              <Link
                to={`/${slug}/settings/administration/subscription/plans`}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:text-slate-900 dark:text-slate-300 dark:hover:text-white max-sm:px-0"
              >
                Compare plans
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>

          {/* Hero icon medallion */}
          <div className="relative hidden lg:flex lg:items-start lg:justify-center">
            <div
              className="relative motion-safe:animate-[fadeUp_0.7s_ease-out_both]"
              style={{ animationDelay: '160ms' }}
            >
              <div
                className={cn(
                  'relative flex h-[260px] w-[260px] items-center justify-center rounded-[36px] ring-1 ring-inset',
                  ACCENT_ICON_BG[config.accent],
                )}
              >
                <HeroIcon weight="duotone" className="h-28 w-28" />
                {/* Floating required-plan tag */}
                <span className="absolute -right-3 -top-3 inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-amber-950 shadow-md">
                  <SparkleIcon weight="fill" className="h-3 w-3" /> {planName}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Feature grid */}
        <div className="mt-16 grid gap-px overflow-hidden rounded-2xl bg-slate-200/60 dark:bg-white/5 sm:grid-cols-2">
          {config.features.map((feat, idx) => {
            const Icon = feat.icon
            return (
              <div
                key={feat.title}
                className="relative bg-[#FAF7F1] p-6 motion-safe:animate-[fadeUp_0.5s_ease-out_both] dark:bg-slate-950 sm:p-7"
                style={{ animationDelay: `${260 + idx * 80}ms` }}
              >
                <div
                  className={cn(
                    'inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1 ring-inset',
                    ACCENT_ICON_BG[config.accent],
                  )}
                >
                  <Icon weight="duotone" className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-serif text-xl font-medium tracking-tight text-slate-900 dark:text-slate-50">
                  {withPlanName(feat.title)}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  {withPlanName(feat.body)}
                </p>
              </div>
            )
          })}
        </div>

        {/* Mock preview */}
        {config.preview ? (
          <div className="mt-16">
            <div className="mb-5 flex items-end justify-between">
              <div>
                <p className="font-mono text-xs font-medium text-slate-400">
                  A peek at
                </p>
                <h2 className="mt-1 font-serif text-3xl font-medium tracking-tight text-slate-900 dark:text-slate-50 sm:text-4xl">
                  {title} in action
                </h2>
                {config.preview.caption && (
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                    {config.preview.caption}
                  </p>
                )}
              </div>
            </div>
            <MockTable
              preview={config.preview}
              accent={config.accent}
              planName={planName}
            />
          </div>
        ) : null}

        {/* Bottom CTA strip */}
        <div className="mt-16 flex flex-col items-start justify-between gap-6 rounded-2xl border border-slate-200/80 bg-white p-7 dark:border-white/5 dark:bg-slate-900/40 sm:flex-row sm:items-center">
          <div>
            <p className="font-mono text-xs font-medium text-slate-400">
              Ready when you are
            </p>
            <h3 className="mt-1 font-serif text-2xl font-medium tracking-tight text-slate-900 dark:text-slate-50">
              {unlockLine}
            </h3>
          </div>
          <Link
            to={`/${slug}/settings/administration/subscription/plans`}
            className="transition-tonal group inline-flex shrink-0 items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            See plans
            <ArrowRightIcon
              weight="bold"
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
            />
          </Link>
        </div>
      </div>

      {/* Local keyframes — Tailwind doesn't ship `fadeUp` by default */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
